import React, { useEffect, useState } from 'react';
import FilterMultiSelect from './FilterMultiSelect';
import { normalizeFocusPreferences } from '../data/focusPreferences';

export default function FocusPreferencesPanel({
  preferences,
  statusOptions,
  stageOptions,
  priorityOptions = [],
  areaOptions = [],
  sortOptions,
  presetOptions,
  customPresetOptions,
  onSave
}) {
  const [draft, setDraft] = useState(() => normalizeFocusPreferences(preferences));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(normalizeFocusPreferences(preferences));
  }, [preferences]);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    try {
      await onSave(normalizeFocusPreferences(draft));
      setMessage('Preferências de foco atualizadas.');
    } catch (requestError) {
      setError(requestError?.message || 'Não foi possível salvar as preferências.');
    } finally {
      setSaving(false);
    }
  };

  const addCustomPreset = () => {
    const id = `custom_foco_${Date.now().toString(36)}`;
    setDraft((current) => ({
      ...current,
      customPresets: [
        ...(current.customPresets || []),
        {
          id,
          label: 'Novo foco',
          q: '',
          statuses: [],
          stages: [],
          priorities: [],
          areas: [],
          assigneeMode: 'any',
          attentionOnly: false
        }
      ].slice(0, 10)
    }));
  };

  const updateCustomPreset = (id, updates) => {
    setDraft((current) => ({
      ...current,
      customPresets: (current.customPresets || []).map((preset) => (
        preset.id === id ? { ...preset, ...updates } : preset
      ))
    }));
  };

  const removeCustomPreset = (id) => {
    setDraft((current) => ({
      ...current,
      defaultPreset: current.defaultPreset === id ? 'all' : current.defaultPreset,
      customPresets: (current.customPresets || []).filter((preset) => preset.id !== id)
    }));
  };

  return (
    <details className="panel-section focus-preferences-panel mt-4" open>
      <summary className="focus-preferences-panel__summary">
        <div>
          <div className="eyebrow">Meu foco</div>
          <strong>Configurar minha visão de trabalho</strong>
        </div>
        <span className="small text-muted">Abrir</span>
      </summary>

      <div className="focus-preferences-panel__body">
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Preset padrão</label>
            <select className="form-select" value={draft.defaultPreset} onChange={(event) => setDraft((current) => ({ ...current, defaultPreset: event.target.value }))}>
              {(customPresetOptions || presetOptions).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Ordenação padrão</label>
            <select className="form-select" value={draft.defaultSort} onChange={(event) => setDraft((current) => ({ ...current, defaultSort: event.target.value }))}>
              {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Limite no foco</label>
            <input
              className="form-control"
              type="number"
              min="1"
              max="12"
              value={draft.focusCardLimit}
              onChange={(event) => setDraft((current) => ({ ...current, focusCardLimit: event.target.value }))}
            />
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-md-4">
            <label className="form-label">Amarelo após</label>
            <input
              className="form-control"
              type="number"
              min="1"
              max="240"
              value={draft.staleHours.level1}
              onChange={(event) => setDraft((current) => ({
                ...current,
                staleHours: { ...current.staleHours, level1: event.target.value }
              }))}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Laranja após</label>
            <input
              className="form-control"
              type="number"
              min="1"
              max="240"
              value={draft.staleHours.level2}
              onChange={(event) => setDraft((current) => ({
                ...current,
                staleHours: { ...current.staleHours, level2: event.target.value }
              }))}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Vermelho após</label>
            <input
              className="form-control"
              type="number"
              min="1"
              max="240"
              value={draft.staleHours.level3}
              onChange={(event) => setDraft((current) => ({
                ...current,
                staleHours: { ...current.staleHours, level3: event.target.value }
              }))}
            />
          </div>
        </div>

        <div className="form-check mt-3">
          <input
            id="focus-isolate-on-open"
            className="form-check-input"
            type="checkbox"
            checked={draft.isolateOnOpen}
            onChange={(event) => setDraft((current) => ({ ...current, isolateOnOpen: event.target.checked }))}
          />
          <label className="form-check-label" htmlFor="focus-isolate-on-open">
            Isolar a tarefa escolhida quando eu estiver em modo foco
          </label>
        </div>

        <div className="focus-preferences-panel__filters mt-3">
          <FilterMultiSelect
            label="Ocultar status"
            options={statusOptions}
            selectedValues={draft.hiddenStatuses}
            onChange={(values) => setDraft((current) => ({ ...current, hiddenStatuses: values }))}
            allLabel="Todos os status"
            noneLabel="Nenhum status oculto"
          />

          <FilterMultiSelect
            label="Ocultar etapas"
            options={stageOptions}
            selectedValues={draft.hiddenStages}
            onChange={(values) => setDraft((current) => ({ ...current, hiddenStages: values }))}
            allLabel="Todas as etapas"
            noneLabel="Nenhuma etapa oculta"
          />
        </div>

        <div className="focus-custom-presets mt-4">
          <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
            <div>
              <h3 className="mb-1">Meus botões de foco</h3>
              <div className="small text-muted">Crie até 10 atalhos pessoais para reduzir a lista por contexto.</div>
            </div>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={addCustomPreset}>
              Novo botão
            </button>
          </div>

          <div className="focus-custom-presets__list mt-3">
            {(draft.customPresets || []).map((preset) => (
              <div className="focus-custom-card" key={preset.id}>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Nome do botão</label>
                    <input
                      className="form-control"
                      value={preset.label}
                      onChange={(event) => updateCustomPreset(preset.id, { label: event.target.value })}
                      placeholder="Ex.: Comercial retorno"
                    />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Busca textual</label>
                    <input
                      className="form-control"
                      value={preset.q}
                      onChange={(event) => updateCustomPreset(preset.id, { q: event.target.value })}
                      placeholder="Ex.: aguardando retorno"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Responsável</label>
                    <select
                      className="form-select"
                      value={preset.assigneeMode || 'any'}
                      onChange={(event) => updateCustomPreset(preset.id, { assigneeMode: event.target.value })}
                    >
                      <option value="any">Qualquer</option>
                      <option value="me">Só minhas</option>
                      <option value="unassigned">Sem responsável</option>
                    </select>
                  </div>
                </div>

                <div className="focus-preferences-panel__filters mt-3">
                  <FilterMultiSelect
                    label="Status"
                    options={statusOptions}
                    selectedValues={preset.statuses || []}
                    onChange={(values) => updateCustomPreset(preset.id, { statuses: values })}
                    allLabel="Todos os status"
                    noneLabel="Sem filtro de status"
                  />
                  <FilterMultiSelect
                    label="Etapas"
                    options={stageOptions}
                    selectedValues={preset.stages || []}
                    onChange={(values) => updateCustomPreset(preset.id, { stages: values })}
                    allLabel="Todas as etapas"
                    noneLabel="Sem filtro de etapa"
                  />
                  <FilterMultiSelect
                    label="Prioridades"
                    options={priorityOptions}
                    selectedValues={preset.priorities || []}
                    onChange={(values) => updateCustomPreset(preset.id, { priorities: values })}
                    allLabel="Todas as prioridades"
                    noneLabel="Sem filtro de prioridade"
                  />
                  <FilterMultiSelect
                    label="Áreas"
                    options={areaOptions}
                    selectedValues={preset.areas || []}
                    onChange={(values) => updateCustomPreset(preset.id, { areas: values })}
                    allLabel="Todas as áreas"
                    noneLabel="Sem filtro de área"
                  />
                </div>

                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-3">
                  <label className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={Boolean(preset.attentionOnly)}
                      onChange={(event) => updateCustomPreset(preset.id, { attentionOnly: event.target.checked })}
                    />
                    <span className="form-check-label ms-2">Somente tarefas que precisam de atenção</span>
                  </label>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeCustomPreset(preset.id)}>
                    Remover
                  </button>
                </div>
              </div>
            ))}
            {!draft.customPresets?.length && (
              <div className="empty-state">Nenhum botão personalizado criado ainda.</div>
            )}
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-3">
          <div className="small text-muted">Essas preferências ficam salvas só para o seu usuário.</div>
          <button type="button" className="btn btn-outline-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar meu foco'}
          </button>
        </div>

        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        {message && <div className="alert alert-success mt-3 mb-0">{message}</div>}
      </div>
    </details>
  );
}
